// Each WhatsApp number's health: Meta's quality rating, the business
// portfolio's daily limit on new customers, and this month's service
// messages. Shown in the sidebar, and under More on phones.
import { AlertTriangle, CircleCheck, CircleHelp, CircleX, Unplug } from 'lucide-react'
import { useMeter, type MeterEntry } from '../api/inbox'
import { RequestFailed } from '../api/client'
import { fullTime } from '../lib/format'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

// Meta's limit is per business portfolio: every number shares it.
const limits: Record<MeterEntry['messagingLimit'], string> = {
  TIER_50: 'Can message up to 50 new customers a day, shared by all your numbers.',
  TIER_250: 'Can message up to 250 new customers a day, shared by all your numbers.',
  TIER_2K: 'Can message up to 2,000 new customers a day, shared by all your numbers.',
  TIER_10K: 'Can message up to 10,000 new customers a day, shared by all your numbers.',
  TIER_100K: 'Can message up to 1,00,000 new customers a day, shared by all your numbers.',
  TIER_UNLIMITED: 'No daily limit on messaging new customers.',
  TIER_NOT_SET: 'Daily limit is set after your first message to a new customer.',
  unknown: 'Daily limit not reported by Meta yet.',
}

function Quality({ e }: { e: MeterEntry }) {
  if (e.numberStatus === 'disconnected') {
    return <Badge variant="danger"><Unplug aria-hidden="true" />Disconnected</Badge>
  }
  if (e.numberStatus === 'paused') {
    return <Badge variant="danger"><CircleX aria-hidden="true" />Paused by Meta</Badge>
  }
  switch (e.qualityRating) {
    case 'green':
      return <Badge variant="success"><CircleCheck aria-hidden="true" />High quality</Badge>
    case 'yellow':
      return <Badge variant="attention"><AlertTriangle aria-hidden="true" />Medium quality</Badge>
    case 'red':
      return <Badge variant="danger"><CircleX aria-hidden="true" />Low quality</Badge>
    default:
      return <Badge variant="neutral"><CircleHelp aria-hidden="true" />Quality not known yet</Badge>
  }
}

export function NumberHealth({ className }: { className?: string }) {
  const meter = useMeter()
  if (meter.isPending) {
    return (
      <div role="status" aria-label="Loading number health" className={cn('flex flex-col gap-2 rounded-xl border p-3', className)}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-full" />
      </div>
    )
  }
  if (meter.isError) {
    return <p role="alert" className={cn('rounded-lg bg-destructive-soft p-3 text-xs text-destructive', className)}>{(meter.error as RequestFailed).message}</p>
  }
  if (meter.data.length === 0) {
    return <p className={cn('rounded-xl border p-3 text-xs text-muted-foreground', className)}>No WhatsApp number is connected yet.</p>
  }
  return (
    <section aria-label="WhatsApp number health" className={cn('flex flex-col gap-3 rounded-xl border p-3 text-xs', className)}>
      {meter.data.map((e) => {
        const over = Math.max(0, e.serviceUsed - e.freeTier)
        return (
          <div key={e.phoneNumberId} className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <span className="font-semibold tabular-nums">+{e.displayNumber}</span>
              <Quality e={e} />
            </div>
            <p className="text-muted-foreground">{limits[e.messagingLimit]}</p>
            <p className="tabular-nums text-muted-foreground">
              <span className="font-semibold text-foreground">{e.serviceUsed.toLocaleString('en-IN')}</span> of {e.freeTier.toLocaleString('en-IN')} free service messages used this month
              {over > 0 && `, ${over.toLocaleString('en-IN')} billed at ${e.currency} ${Number(e.rate)} each`}.
            </p>
            {e.qualityCheckedAt && <p className="text-muted-foreground">Quality checked with Meta {fullTime(e.qualityCheckedAt)}.</p>}
          </div>
        )
      })}
    </section>
  )
}
